import { TransportErrors } from '../../enums/errors';
import { XResolveError } from '../../error/xresolve';
const RESOLVER_URL = 'https://apresolve.spotify.com/';
const WellKnownMapping = {
    DEALER: 'dealer',
    WEBGATE: 'spclient',
};
const WEB_API = 'https://api.spotify.com/';
/**
 * Creates a new XResolve Provider for use as an EndpointsProvider for a
 * Transport instance.
 *
 * Note that this function is a factory for an EndpointsProvider function. You
 * must call it prior to giving it to Transport
 *
 * @example
 * // INCORRECT
 * createTransport({
 *   providers: {
 *     endpoints: createProvider // WRONG!
 *   }
 * });
 *
 * // Correct
 * createTransport({
 *   providers: {
 *     endpoints: createProvider() // Works!
 *   }
 * });
 *
 * @param mappings - A map of endpoint types to the desired endpoint names. By
 *   default, the mappings used are {DEALER: 'dealer', WEBGATE: 'webgate'}. An
 *   application can change the type of dealer or webgate to use using this
 *   mapping. For example, setting {WEBGATE: 'exp'} will make the XResolve
 *   provider request experimental webgate endpoints instead.
 * @return An EndpointsProvider function that can be passed to the
 *   createTransport() function.
 */
export function createProvider(mappings = {}) {
    const types = {
        dealer: mappings.dealer || WellKnownMapping.DEALER,
        webgate: mappings.webgate || WellKnownMapping.WEBGATE,
    };
    const url = `${RESOLVER_URL}?type=${types.dealer}&type=${types.webgate}`;
    return function (transport) {
        return transport
            .request(url, {
            forcePolyfill: true,
            responseType: 'json',
            retry: {
                maxRetries: 5,
                condition: function (response, statusFamily) {
                    // We have a fairly strict 2xx requirement here, since we want to
                    // ensure that we can connect.
                    return response.getStatusFamily() !== statusFamily.SUCCESS;
                },
            },
            metadata: {
                // Ensure that this goes through always.
                noRequestTransform: true,
            },
        })
            .then((response) => {
            if (response.status !== 200) {
                throw new XResolveError(TransportErrors.XRESOLVE_REQUEST_FAILED_WITH_STATUS, `X-Resolve responded with status${response.status}`, response.status);
            }
            const body = response.body;
            if (!body) {
                throw new XResolveError(TransportErrors.XRESOLVE_INVALID_RESPONSE, 'X-Resolve responded with empty/invalid body.', response.status);
            }
            const result = {
                dealer: body[types.dealer] && body[types.dealer][0],
                webgate: body[types.webgate] && body[types.webgate][0],
                webapi: WEB_API,
            };
            if (!result.dealer || !result.webgate) {
                throw new XResolveError(TransportErrors.XRESOLVE_INCOMPLETE_RESPONSE, 'X-Resolve responded with incomplete results.', response.status);
            }
            return result;
        })
            .then((result) => {
            // Ensure that we prefix with protocols.
            result.dealer = `wss://${result.dealer.replace(/:443$/, '')}`;
            result.webgate = `https://${result.webgate.replace(/:443$/, '')}`;
            return result;
        });
    };
}
//# sourceMappingURL=xresolve.js.map